const AnalyticsWebClient = require('@atlassiansox/analytics-web-client');
const Sentry = require('@sentry/browser');
const _ = require('lodash');

function scrubMessage(message) {
  return message
    .replace(/token=[0-9a-fA-F/%]*/g, 'token=<TOKEN>')
    .replace(/"X-Butler-Trello-Token":\s*"[0-9a-fA-F]*"/g, '"X-Butler-Trello-Token": "<TOKEN>"')
    .replace(/[0-9a-fA-F]{64}/g, '<TOKEN>')
    .replace(/(ATT[A-Z])[A-Za-z0-9/-]{16,}([A-F0-9]{8})/g, '<TOKEN>');
}

let analyticsClientPromise;
let EnvironmentVariableType = 'prod';
EnvironmentVariableType = (EnvironmentVariableType === 'test'
  ? 'staging'
  : EnvironmentVariableType
).toUpperCase();

/**
 * Retrieves client origin and user agent and returns as
 * AnalyticsWebClient.originType and AnalyticsWebClient.platformType strings
 */
function getOriginAndUserAgent() {
  const env = {};
  if (/Electron/.test(window.navigator.userAgent)) {
    env.origin = AnalyticsWebClient.originType.DESKTOP;
    if (/Macintosh/.test(window.navigator.userAgent)) {
      env.platform = AnalyticsWebClient.platformType.MAC;
    } else if (/Windows/.test(window.navigator.userAgent)) {
      env.platform = AnalyticsWebClient.platformType.WINDOWS;
    } else if (/Linux/.test(window.navigator.userAgent)) {
      env.platform = AnalyticsWebClient.platformType.LINUX;
    } else {
      env.platform = AnalyticsWebClient.platformType.WEB;
    }
  } else {
    env.origin = AnalyticsWebClient.originType.WEB;
    env.platform = AnalyticsWebClient.platformType.WEB;
  }
  return env;
}

/**
 * returns a Promise which resolves in a new initialized AnalyticsWebClient obj.
 * result is cached in [analyticsClientPromise]
 */
function getAnalyticsWebClient() {
  if (!analyticsClientPromise) {
    analyticsClientPromise = window._trello.member('id').then(function(m) {
      const { origin, platform } = getOriginAndUserAgent();
      const apiHost =
        EnvironmentVariableType === 'PROD'
          ? 'trello.com/gateway/api/gasv3/api/v1'
          : 'trellis.coffee/gateway/api/gasv3/api/v1';
      const analyticsClient = new AnalyticsWebClient.default(
        {
          env: AnalyticsWebClient.envType[EnvironmentVariableType],
          product: 'trello',
          origin,
          platform,
          locale: window.locale || 'en-US',
        },
        {
          apiHost,
          flushBeforeUnload: window._modernization,
          flushWaitInterval: window._modernization ? 0 : false,
        }
      );
      analyticsClient.setTenantInfo(AnalyticsWebClient.tenantType.NONE);
      if (m && m.id !== 'notLoggedIn') {
        const trelloContext = window._trello.getContext();
        if (trelloContext.memberAaId) {
          analyticsClient.setUserInfo(
            AnalyticsWebClient.userType.ATLASSIAN_ACCOUNT,
            trelloContext.memberAaId
          );
        } else {
          analyticsClient.setUserInfo(AnalyticsWebClient.userType.TRELLO, m.id);
        }

        analyticsClient.setUIViewedAttributes({ trelloMemberId: m.id });
      }

      const { organization: workspaceId } = window._trello.getContext();
      if (workspaceId) {
        analyticsClient.setWorkspaceInfo(workspaceId);
      }
      return analyticsClient;
    });
  }
  return analyticsClientPromise;
}

function _normalizeContainers(containers) {
  return _(containers)
    .pick('enterprise', 'organization', 'board', 'list', 'card')
    .pickBy(val => val && val.id)
    .mapValues(({ id }) => ({ id }))
    .value();
}

/**
 * Retrieves container information for analytics. e.g. board and org ids
 *
 * @param {*} trello A reference to Trello e.g. TrelloPowerUp.iframe()
 */
function getContainers(trello = window._trello) {
  const ctx = trello && typeof trello.getContext === 'function' && trello.getContext();
  if (!ctx || !ctx.board) {
    return {};
  }
  return _normalizeContainers({
    board: { id: ctx.board },
    organization: { id: ctx.organization },
  });
}

/**
 * Retrieves Trello member ID and adds to event attributes for analytics
 *
 * @param {*} event is the event payload
 */
function getAttributes(event) {
  return { trelloMemberId: window._trello.getContext().member, ...event.attributes };
}

/**
 * Wrapper for [sendUIEvent] that ensures AnalyticsWebClient obj is initialized.
 *
 * @param {*} event is the event payload
 */
function sendUIEvent(event) {
  return getAnalyticsWebClient().then(client =>
    client.sendUIEvent({
      containers: getContainers(),
      ...event, // ordering is intentional to prevent attributes from getting overwritten in spread
      attributes: getAttributes(event),
    })
  );
}

/**
 * Wrapper for [sendTrackEvent] that ensures AnalyticsWebClient obj is initialized.
 *
 * @param {*} event is the event payload
 */
function sendTrackEvent(event) {
  return getAnalyticsWebClient().then(client =>
    client.sendTrackEvent({
      containers: getContainers(),
      ...event, // ordering is intentional to prevent attributes from getting overwritten in spread
      attributes: getAttributes(event),
    })
  );
}

/**
 * Wrapper for [sendScreenEvent] that ensures AnalyticsWebClient obj is initialized.
 *
 * @param {*} event is the event payload
 */
function sendScreenEvent(event) {
  return getAnalyticsWebClient().then(client =>
    client.sendScreenEvent({
      containers: getContainers(),
      ...event, // ordering is intentional to prevent attributes from getting overwritten in spread
      attributes: getAttributes(event),
    })
  );
}

/**
 * Wrapper for [sendOperationalEvent] that ensures AnalyticsWebClient obj is initialized.
 *
 * @param {*} event is the event payload
 */
function sendOperationalEvent(event) {
  return getAnalyticsWebClient().then(client =>
    client.sendOperationalEvent({
      containers: getContainers(),
      ...event, // ordering is intentional to prevent attributes from getting overwritten in spread
      attributes: getAttributes(event),
    })
  );
}

function createTaskSession(taskName, traceId) {
  return getAnalyticsWebClient().then(client => {
    client.task.createTaskSessionWithProvidedId(taskName, traceId);
  });
}

function completeTaskSession(taskName) {
  return getAnalyticsWebClient().then(client => {
    client.task.completeTaskSession(taskName);
  });
}

function getTaskSessions() {
  return getAnalyticsWebClient().then(client => {
    return client.task.getAllTaskSessions();
  });
}

function getRandomTraceId() {
  // Generates 64 bit string
  // Taken from openzipkin/zipkin-js
  // https://github.com/openzipkin/zipkin-js/blob/50d9c3afb662c2d18d688ecef66883d6c5326f4b/packages/zipkin/src/tracer/randomTraceId.js
  const digits = '0123456789abcdef';
  let n = '';
  for (let i = 0; i < 16; i += 1) {
    const rand = Math.floor(Math.random() * 16);
    n += digits[rand];
  }
  return n;
}

// Tracing
function getTraceId() {
  // Return 128 bit trace ids (32 hexadecimal digits)
  // https://github.com/openzipkin/zipkin-js/blob/98f7796d54199ccb2a81dea04c466a40814ccb24/packages/zipkin/src/tracer/index.js#L77
  // but support B3 single format with fist 32 bits (8 digits) as epoch seconds
  // https://github.com/openzipkin/b3-propagation/blob/master/STATUS.md#epoch128

  const epochSeconds = Math.floor(Date.now() / 1000).toString(16);
  const remainingBits = getRandomTraceId().slice(8) + getRandomTraceId();

  return epochSeconds + remainingBits;
}

function getErrorAttributes(error) {
  const attributes = {};
  if (error.name) {
    attributes.errorName = error.name;
  }
  if (error.message) {
    attributes.errorMessage = scrubMessage(error.message);
  }
  return attributes;
}

function startTask(e) {
  const { taskName, source, containers, attributes } = e;
  if (!taskName) {
    Sentry.captureMessage(`No taskName passed to startTask`);
    return null;
  }
  const traceId = getTraceId();
  createTaskSession(taskName, traceId);
  sendOperationalEvent({
    action: 'taskStart',
    actionSubject: 'ui',
    source: 'ui',
    containers,
    attributes: {
      task: taskName,
      taskId: traceId,
      source,
      ...attributes,
    },
  });
  return traceId;
}

function taskSucceeded(e) {
  const { taskName, traceId, source, containers, attributes } = e;
  if (!traceId) {
    Sentry.captureMessage(`No traceID passed to taskSucceeded`);
    return null;
  }
  if (!taskName) {
    Sentry.captureMessage(`No taskName passed to taskSucceeded`);
    return null;
  }
  completeTaskSession(taskName);
  sendOperationalEvent({
    action: 'taskSuccess',
    actionSubject: 'ui',
    source: 'ui',
    containers,
    attributes: {
      task: taskName,
      taskId: traceId,
      source,
      ...attributes,
    },
  });
  return traceId;
}

function taskFailed(e) {
  const { taskName, traceId, source, containers, attributes, error } = e;
  if (!traceId) {
    Sentry.captureMessage(`No traceID passed to taskFailed`);
    return null;
  }
  if (!taskName) {
    Sentry.captureMessage(`No taskName passed to taskFailed`);
    return null;
  }
  completeTaskSession(taskName);
  const errorAttributes = getErrorAttributes(error);
  sendOperationalEvent({
    action: 'taskFail',
    actionSubject: 'ui',
    source: 'ui',
    containers,
    attributes: {
      task: taskName,
      taskId: traceId,
      source,
      ...attributes,
      ...errorAttributes,
    },
  });
  return traceId;
}

function taskAborted(e) {
  const { taskName, traceId, source, containers, attributes } = e;
  if (!traceId) {
    Sentry.captureMessage(`No traceID passed to taskAborted`);
    return null;
  }
  if (!taskName) {
    Sentry.captureMessage(`No taskName passed to taskAborted`);
    return null;
  }
  completeTaskSession(taskName);
  sendOperationalEvent({
    action: 'taskAbort',
    actionSubject: 'ui',
    source: 'ui',
    containers,
    attributes: {
      task: taskName,
      taskId: traceId,
      source,
      ...attributes,
    },
  });
  return traceId;
}

module.exports = {
  sendUIEvent,
  sendTrackEvent,
  sendScreenEvent,
  startTask,
  taskSucceeded,
  taskFailed,
  taskAborted,
  getTaskSessions,
};
